import { useEffect, useState, useContext } from "react";
import { toast } from "react-toastify";
import {
    createProductProcessing,
    updateProductProcessing,
    getProductMaster,
    get_product_details,
    getRawMaterials,
} from "../../../Utility/Api";
import { WarehouseContext } from "../../../Utility/UserContext";

export const EditProductProcessing = ({
    showModal,
    setShowModal,
    fetchProducts,
    selectedProduct,
    AddorEdit,
    setCurrentPage,
}) => {
    const { showLoader, hideLoader, UserItem } = useContext(WarehouseContext);

    const [formData, setFormData] = useState({
        product_id: "",
        created_by: UserItem?.user_id || "EMP001",
    });

    const [errors, setErrors] = useState({});
    const [ingredientsList, setIngredientsList] = useState([]);
    const [productList, setProductList] = useState([]);
    const [rawMaterialList, setRawMaterialList] = useState([]);

    useEffect(() => {
        fetchProductsList();
        fetchRawMaterialsList();

        if (selectedProduct) {
            console.log("Selected product:", selectedProduct);
            setFormData({
                product_id: selectedProduct?.product_id || "",
                created_by: UserItem?.user_id || "EMP001",
            });
            
            try {
                if (selectedProduct.ingrident && Array.isArray(selectedProduct.ingrident)) {
                    setIngredientsList(selectedProduct.ingrident);
                } else if (selectedProduct.ingridents) {
                    setIngredientsList(JSON.parse(selectedProduct.ingridents));
                } else {
                    setIngredientsList([]);
                }
            } catch (error) {
                console.error("Error parsing ingredients:", error);
                setIngredientsList([]);
            }
        } else {
            resetForm();
        }
    }, [selectedProduct]);

    const fetchProductsList = async () => {
        try {
            const response = await getProductMaster();
            if (response.data && response.data.data) {
                setProductList(response.data.data);
            }
        } catch (err) {
            console.error("Error fetching products:", err);
            toast.error("Failed to fetch products");
        }
    };

    const fetchRawMaterialsList = async () => {
        try {
            const response = await getRawMaterials();
            if (response.data && response.data.data) {
                setRawMaterialList(response.data.data);
            }
        } catch (err) {
            console.error("Error fetching raw materials:", err);
            toast.error("Failed to fetch raw materials");
        }
    };

    // Validation function for ingredients
    const validateIngredients = () => {
        if (ingredientsList.length === 0) {
            toast.error("At least one ingredient is required");
            return false;
        }
        
        // Check for empty fields
        for (let i = 0; i < ingredientsList.length; i++) {
            const ing = ingredientsList[i];
            if (!ing.id || !ing.qty || !ing.unit) {
                toast.error(`All fields are required for ingredient #${i + 1}`);
                return false;
            }
        }

        // Check for duplicate ingredients
        const uniqueIngredientIds = new Set();
        for (let i = 0; i < ingredientsList.length; i++) {
            if (uniqueIngredientIds.has(ingredientsList[i].id)) {
                toast.error("Duplicate ingredients are not allowed");
                return false;
            }
            uniqueIngredientIds.add(ingredientsList[i].id);
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        showLoader();

        const validationErrors = {};
        if (!formData.product_id) validationErrors.product_id = "Product is required.";

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            hideLoader();
            return;
        }

        // Validate ingredients
        if (!validateIngredients()) {
            hideLoader();
            return;
        }

        // Prepare the exact payload format needed by the API
        const payload = {
            product_id: formData.product_id,
            ingridents: JSON.stringify(ingredientsList),
            created_by: formData.created_by,
        };

        console.log("Submitting payload:", payload);

        try {
            if (AddorEdit) {
                const response = await createProductProcessing(payload);
                console.log("Create response:", response);
                toast.success("Product processing added successfully");
            } else {
                const response = await updateProductProcessing(selectedProduct?.product_processing_id, payload);
                console.log("Update response:", response);
                toast.success("Product processing updated successfully");
            }
            fetchProducts();
            handleCancel();
        } catch (error) {
            console.error("Error saving product processing:", error);
            toast.error(error?.response?.data?.message || "Error saving product processing");
        } finally {
            hideLoader();
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        setErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    const handleIngredientChange = (index, field, value) => {
        const updatedIngredients = [...ingredientsList];
        updatedIngredients[index][field] = value;
        setIngredientsList(updatedIngredients);
    };

    const addIngredient = () => {
        const defaultUnit = ingredientsList.length > 0 && ingredientsList[0].unit 
            ? ingredientsList[0].unit 
            : "grams";
            
        setIngredientsList([
            ...ingredientsList, 
            { id: "", qty: "", unit: defaultUnit }
        ]);
    };

    const removeIngredient = (index) => {
        const updatedIngredients = [...ingredientsList];
        updatedIngredients.splice(index, 1);
        setIngredientsList(updatedIngredients);
    };

    const resetForm = () => {
        setFormData({
            product_id: "",
            created_by: UserItem?.user_id || "EMP001",
        });
        setIngredientsList([]);
        setErrors({});
    };

    const handleCancel = () => {
        setShowModal(false);
        resetForm();
    };

    // Calculate total ingredients quantity
    const totalIngredientsQty = ingredientsList.reduce(
        (sum, ing) => sum + parseFloat(ing.qty || 0),
        0
    );

    return (
        <>
            {showModal && (
                <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
                    <div className="modal-dialog modal-md">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" style={{ fontFamily: "poppins" }}>
                                    {selectedProduct ? "Edit Product Processing" : "Add Product Processing"}
                                </h5>
                                <button type="button" className="btn-close" onClick={handleCancel}></button>
                            </div>
                            <div className="modal-body" style={{ fontFamily: "poppins" }}>
                                <form onSubmit={handleSubmit} className="row g-3">
                                    {/* Select Product */}
                                    <div className="col-md-12">
                                        <label>Select Product <span className="text-danger">*</span></label>
                                        <select
                                            name="product_id"
                                            value={formData.product_id}
                                            onChange={(e) => {
                                                handleChange(e);
                                                const selected = productList.find(
                                                    (p) => String(p.product_id) === String(e.target.value)
                                                );
                                                if (selected) {
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        product_name: selected.product_title || ""
                                                    }));
                                                }
                                            }}
                                            className={`form-control ${errors.product_id ? 'is-invalid' : ''}`}
                                        >
                                            <option value="">Select Product</option>
                                            {productList.map((prod) => (
                                                <option key={prod.product_id} value={prod.product_id}>
                                                    {prod.title}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.product_id && <div className="invalid-feedback">{errors.product_id}</div>}
                                    </div>

                                    {/* Ingredients */}
                                    <div className="col-md-12 mt-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <label>Ingredients <span className="text-danger">*</span></label>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                onClick={addIngredient}
                                            >
                                                Add Ingredient
                                            </button>
                                        </div>
                                        
                                        {ingredientsList.length > 0 ? (
                                            <>
                                                {ingredientsList.map((ing, index) => (
                                                    <div key={index} className="d-flex gap-2 mb-2">
                                                        <select
                                                            value={ing.id}
                                                            onChange={(e) => {
                                                                const raw = rawMaterialList.find(
                                                                    (rm) => String(rm.id) === String(e.target.value)
                                                                );
                                                                if (raw) {
                                                                    handleIngredientChange(index, "id", raw.id);
                                                                    handleIngredientChange(index, "qty", raw.default_qty || "");
                                                                }
                                                            }}
                                                            className="form-control"
                                                        >
                                                            <option value="">Select Raw Material</option>
                                                            {rawMaterialList.map((rm) => (
                                                                <option key={rm.id} value={rm.id}>
                                                                    {rm.title}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <input
                                                            type="number"
                                                            placeholder="Qty"
                                                            value={ing.qty}
                                                            onChange={(e) =>
                                                                handleIngredientChange(index, "qty", e.target.value)
                                                            }
                                                            className="form-control"
                                                        />
                                                        <select
                                                            value={ing.unit}
                                                            onChange={(e) => handleIngredientChange(index, "unit", e.target.value)}
                                                            className="form-control"
                                                        >
                                                            <option value="grams">Grams</option>
                                                            <option value="kg">Kilograms</option>
                                                            <option value="liters">Liters</option>
                                                            <option value="milliliters">Milliliters</option>
                                                            <option value="pieces">Pieces</option>
                                                        </select>
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger"
                                                            onClick={() => removeIngredient(index)}
                                                        >
                                                            X
                                                        </button>
                                                    </div>
                                                ))}

                                                {/* Ingredients Total */}
                                                <div className="alert alert-info mt-3">
                                                    <div className="d-flex justify-content-between">
                                                        <span>Total Ingredients:</span>
                                                        <strong>
                                                            {totalIngredientsQty.toFixed(2)} {ingredientsList[0]?.unit || ''}
                                                        </strong>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="alert alert-warning">
                                                No ingredients added yet. Click "Add Ingredient" to start.
                                            </div>
                                        )}
                                    </div>

                                    {/* Buttons */}
                                    <div className="col-12 text-end mt-4">
                                        <button type="button" className="btn btn-secondary me-2" onClick={handleCancel}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-success">
                                            {selectedProduct ? "Update" : "Save"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
